/**
 * Portée d'un défi privé (migration 0722) : tout le programme de l'année, un pack,
 * une ou plusieurs SKILLCARDS, ou un exercice. Les cartes proposées sont celles des
 * packs publiés du niveau et de la matière (`get_curriculum_timeline`) ; les exercices
 * d'une carte viennent de sa page publique, avec le cadenas des exercices payants
 * (un compte gratuit ne joue que l'essai gratuit de chaque carte).
 */

import { useState } from 'react';
import { Check, Layers, Lock } from 'lucide-react';
import { MathText } from '@jeraisonne/ui';
import { PickerField } from '@/components/pickers/picker-modal';
import { SkillcardChip } from '@/features/skillcards/skillcard-chip';
import {
  SkillcardCatalogModal,
  cardCountLabel,
  matchesCardQuery,
  type CatalogCard,
} from '@/features/skillcards/skillcard-catalog-modal';
import { usePublicCard } from '@/lib/cards/api';
import { useSkillcardAccess } from '@/lib/skillcards/purchase';
import { exerciseLockState } from '@/features/cards/card-exercises';
import type { TimelinePackRow } from '@/lib/curriculum/api';
import { PRIVATE_LIVE_MAX_CARDS, type PrivateLiveScope } from '@/lib/private-live';

export interface ScopeCard {
  id: string;
  title: string;
}

export interface ScopeCardSection {
  key: string;
  label: string;
  cards: ScopeCard[];
}

/** Cartes des packs, rangées par pack ; une carte n'apparaît que dans son premier pack. PUR. */
export function cardSectionsFromPacks(
  packs: readonly Pick<TimelinePackRow, 'id' | 'title' | 'cards'>[],
): ScopeCardSection[] {
  const seen = new Set<string>();
  const out: ScopeCardSection[] = [];
  for (const p of packs) {
    const cards = p.cards
      .filter((c) => !seen.has(c.id))
      .map((c) => {
        seen.add(c.id);
        return { id: c.id, title: c.title };
      });
    if (cards.length > 0) out.push({ key: p.id, label: p.title, cards });
  }
  return out;
}

export { cardCountLabel, matchesCardQuery };

/** Ajoute ou retire une carte (20 au plus). PUR. */
export function toggleCard(ids: readonly string[], id: string): string[] {
  if (ids.includes(id)) return ids.filter((x) => x !== id);
  if (ids.length >= PRIVATE_LIVE_MAX_CARDS) return [...ids];
  return [...ids, id];
}

/**
 * SKILLCARD proposée par défaut : la dernière travaillée par l'élève parmi celles des
 * packs du niveau et de la matière. `null` sans carte travaillée. PUR.
 */
export function lastUsedCardId(
  mine: readonly { id: string; last_used_at: string | null }[],
  sections: readonly ScopeCardSection[],
): string | null {
  const ids = new Set(sections.flatMap((s) => s.cards.map((c) => c.id)));
  let best: { id: string; at: string } | null = null;
  for (const c of mine) {
    if (!c.last_used_at || !ids.has(c.id)) continue;
    if (!best || c.last_used_at > best.at) best = { id: c.id, at: c.last_used_at };
  }
  return best?.id ?? null;
}

/** Du plus précis au plus large : SKILLCARDS d'abord, puis pack, puis programme. */
const SCOPES: { value: PrivateLiveScope; label: string }[] = [
  { value: 'cards', label: 'SKILLCARDS' },
  { value: 'pack', label: 'Pack' },
  { value: 'programme', label: 'Programme' },
  { value: 'exercise', label: 'Exercice' },
];

const SELECT =
  'border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full rounded-xl border px-3 text-sm focus:outline-none';

export function ScopeFields({
  packs,
  scope,
  onScope,
  packId,
  onPackId,
  cardIds,
  onCardIds,
  exerciseCardId,
  exerciseId,
  onExercise,
  userId,
  recent = [],
}: {
  packs: readonly TimelinePackRow[];
  scope: PrivateLiveScope;
  onScope: (s: PrivateLiveScope) => void;
  packId: string;
  onPackId: (id: string) => void;
  cardIds: string[];
  onCardIds: (ids: string[]) => void;
  exerciseCardId: string | null;
  exerciseId: string | null;
  onExercise: (cardId: string | null, exerciseId: string | null, questions?: number) => void;
  userId: string | null;
  /** Dernières SKILLCARDS travaillées par l'élève (en tête du sélecteur). */
  recent?: readonly CatalogCard[];
}) {
  const sections = cardSectionsFromPacks(packs);
  const titles = new Map([
    ...recent.map((c) => [c.id, c.title] as const),
    ...sections.flatMap((s) => s.cards.map((c) => [c.id, c.title] as const)),
  ]);
  const [picker, setPicker] = useState<'cards' | 'exercise-card' | null>(null);

  return (
    <div className="space-y-3">
      <div>
        <p className="text-ink-700 mb-2 text-sm font-semibold">Les questions portent sur</p>
        <div
          className="bg-ink-100 grid grid-cols-2 gap-1 rounded-xl p-1 sm:grid-cols-4"
          role="radiogroup"
          aria-label="Les questions portent sur"
        >
          {SCOPES.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={scope === o.value}
              onClick={() => onScope(o.value)}
              className={`min-h-11 rounded-lg px-2 text-sm font-semibold transition-colors active:scale-[0.98] ${
                scope === o.value
                  ? 'text-primary-700 ocean:bg-primary-500 ocean:text-[#ffffff] bg-[#ffffff] shadow-sm'
                  : 'text-ink-600'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {scope === 'programme' && (
        <p className="text-ink-500 text-xs">Des questions de tout le programme de l’année.</p>
      )}

      {scope === 'pack' && (
        <div className="space-y-1.5">
          <label htmlFor="pl-pack" className="text-ink-700 block text-sm font-semibold">
            Pack
          </label>
          <select
            id="pl-pack"
            value={packId}
            onChange={(e) => onPackId(e.target.value)}
            className={SELECT}
          >
            <option value="">Choisis un pack…</option>
            {packs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </div>
      )}

      {scope === 'cards' && (
        <div className="space-y-2">
          <PickerField
            label="SKILLCARDS"
            placeholder="Choisir les SKILLCARDS…"
            onOpen={() => setPicker('cards')}
            selected={
              cardIds.length > 0 ? (
                <span className="text-ink-900 font-semibold">{cardCountLabel(cardIds.length)}</span>
              ) : null
            }
          />
          {cardIds.length > 0 && (
            <ul className="flex flex-wrap gap-1.5" aria-label="SKILLCARDS choisies">
              {cardIds.map((id) => (
                <li key={id} className="max-w-full">
                  <SkillcardChip cardId={id} fallbackTitle={titles.get(id)} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {scope === 'exercise' && (
        <div className="space-y-2">
          <PickerField
            label="SKILLCARD de l’exercice"
            placeholder="Choisir une SKILLCARD…"
            onOpen={() => setPicker('exercise-card')}
            selected={
              exerciseCardId ? (
                <ExerciseCardTitle cardId={exerciseCardId} fallback={titles.get(exerciseCardId)} />
              ) : null
            }
          />
          {exerciseCardId && (
            <ExerciseChoices
              cardId={exerciseCardId}
              value={exerciseId}
              userId={userId}
              onChange={(id, questions) => onExercise(exerciseCardId, id, questions)}
            />
          )}
        </div>
      )}

      <p className="text-ink-500 text-xs">
        Sans accès complet, les questions viennent de l’essai gratuit de chaque SKILLCARD (et des
        cartes que tu as débloquées).
      </p>

      {picker && (
        <SkillcardCatalogModal
          title={picker === 'cards' ? 'Choisir les SKILLCARDS' : 'Choisir une SKILLCARD'}
          sections={sections}
          recent={recent}
          max={PRIVATE_LIVE_MAX_CARDS}
          multiple={picker === 'cards'}
          selected={picker === 'cards' ? cardIds : exerciseCardId ? [exerciseCardId] : []}
          onToggle={(id) => {
            if (picker === 'cards') onCardIds(toggleCard(cardIds, id));
            else {
              onExercise(id, null);
              setPicker(null);
            }
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}

function ExerciseCardTitle({ cardId, fallback }: { cardId: string; fallback?: string }) {
  const card = usePublicCard(cardId);
  const title = card.data?.card.title ?? fallback ?? 'SKILLCARD';
  return (
    <span className="text-ink-900 font-semibold">
      <MathText>{title}</MathText>
    </span>
  );
}

function ExerciseChoices({
  cardId,
  value,
  userId,
  onChange,
}: {
  cardId: string;
  value: string | null;
  userId: string | null;
  onChange: (exerciseId: string, questions: number) => void;
}) {
  const card = usePublicCard(cardId);
  const access = useSkillcardAccess(cardId, userId);
  const listAccess = access.data
    ? {
        allowed: access.data.allowed,
        freeExerciseId: access.data.free_exercise_id,
        freeQuestions: access.data.free_questions,
      }
    : undefined;
  const exercises = card.data?.exercises ?? [];

  if (card.isLoading) return <p className="text-ink-500 text-sm">Chargement des exercices…</p>;
  if (exercises.length === 0) {
    return <p className="text-ink-500 text-sm">Cette SKILLCARD n’a pas encore d’exercice.</p>;
  }
  return (
    <div role="radiogroup" aria-label="Exercice" className="space-y-1.5">
      {exercises.map((ex) => {
        const lock = exerciseLockState(ex, listAccess);
        const locked = lock.kind === 'locked';
        // Nombre total de questions de l'exercice quand la RPC le donne (migration 0721),
        // sinon celles reliées à la carte.
        const total = (ex as { question_count?: number | null }).question_count;
        const questions = total ?? ex.linked_questions;
        const on = ex.id === value;
        return (
          <button
            key={ex.id}
            type="button"
            role="radio"
            aria-checked={on}
            aria-disabled={locked}
            disabled={locked}
            onClick={() => onChange(ex.id, questions)}
            className={`flex min-h-11 w-full items-center gap-3 rounded-xl border px-3 py-2 text-left text-sm active:scale-[0.99] ${
              on ? 'border-primary-500 bg-primary-50' : 'border-ink-200'
            } ${locked ? 'opacity-50' : ''}`}
          >
            {locked ? (
              <Lock className="text-ink-500 h-4 w-4 shrink-0" aria-label="Verrouillé" />
            ) : (
              <Layers className="text-primary-600 h-4 w-4 shrink-0" aria-hidden />
            )}
            <span className="text-ink-900 min-w-0 flex-1 font-semibold">
              <MathText>{ex.title}</MathText>
            </span>
            <span className="text-ink-500 shrink-0 text-xs">
              {questions} question{questions > 1 ? 's' : ''}
            </span>
            {on && <Check className="text-primary-600 h-4 w-4 shrink-0" aria-hidden />}
          </button>
        );
      })}
    </div>
  );
}
